import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FaSearch, 
  FaFilter, 
  FaDownload, 
  FaClipboardList, 
  FaBuilding, 
  FaArrowLeft
} from 'react-icons/fa';

const AssessmentReports = ({ viewMode }) => {
  const { id } = useParams();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [organizations, setOrganizations] = useState([]);
  const [filteredAssessments, setFilteredAssessments] = useState([]);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [assessmentDetails, setAssessmentDetails] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Fetch assessments and organizations on component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (viewMode === 'details' && id) {
          // Fetch single assessment details
          const assessmentRes = await axios.get(`/api/admin/assessments/${id}`);
          setCurrentAssessment(assessmentRes.data);
          
          // Fetch detailed assessment data including questions and answers
          const detailsRes = await axios.get(`/api/assessments/${id}`);
          setAssessmentDetails(detailsRes.data);
        } else {
          // Fetch assessments
          const assessmentsRes = await axios.get('/api/admin/assessments');
          setAssessments(assessmentsRes.data);
          
          // Fetch organizations for filtering
          const orgsRes = await axios.get('/api/admin/organizations');
          setOrganizations(orgsRes.data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load assessment data. Please try again.');
        setLoading(false);
        toast.error('Failed to load assessment data');
      }
    };
    
    fetchData();
  }, [id, viewMode]);
  
  // Apply filters whenever filter criteria or assessments change
  useEffect(() => {
    if (!Array.isArray(assessments)) {
      setFilteredAssessments([]);
      return;
    }
    
    let filtered = [...assessments];
    
    // Apply search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(assessment => {
        // Check title
        const titleMatch = assessment.title?.toLowerCase().includes(term);
        
        // Check organization name
        const orgMatch = assessment.organization?.name?.toLowerCase().includes(term);
        
        // Check created by name
        const creatorMatch = assessment.createdBy?.name?.toLowerCase().includes(term);
        
        return titleMatch || orgMatch || creatorMatch;
      });
    }
    
    // Apply organization filter
    if (selectedOrgId !== 'all') {
      filtered = filtered.filter(assessment => {
        // Handle both direct _id and nested organization object
        const orgId = assessment.organization?._id || assessment.organization;
        return orgId && orgId.toString() === selectedOrgId;
      });
    }
    
    // Apply status filter
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(assessment => {
        // Handle different status formats
        const status = assessment.status?.toLowerCase() || 'not_started';
        return status === selectedStatus.toLowerCase();
      });
    }
    
    setFilteredAssessments(filtered);
  }, [assessments, searchTerm, selectedOrgId, selectedStatus]);
  
  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };
  
  // Handle organization filter change
  const handleOrgFilterChange = (e) => {
    setSelectedOrgId(e.target.value);
  };
  
  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setSelectedStatus(e.target.value);
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };
  
  // Handle downloading assessment report
  const handleDownloadReport = async (assessmentId, title) => {
    setDownloadLoading(true);
    try {
      // Get assessment details if not already loaded
      let assessmentData;
      if (viewMode === 'details' && currentAssessment && assessmentDetails) {
        assessmentData = { 
          assessment: currentAssessment,
          details: assessmentDetails
        };
      } else {
        // Fetch assessment details
        const assessmentRes = await axios.get(`/api/admin/assessments/${assessmentId}`);
        const detailsRes = await axios.get(`/api/assessments/${assessmentId}`);
        assessmentData = {
          assessment: assessmentRes.data,
          details: detailsRes.data
        };
      }
      
      // Format the assessment data for download
      const formattedData = formatAssessmentForDownload(assessmentData);
      
      // Create a blob and download
      const blob = new Blob([formattedData], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title || 'assessment'}_report.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Assessment report downloaded successfully');
    } catch (err) {
      console.error('Error downloading assessment report:', err);
      toast.error('Failed to download assessment report');
    } finally {
      setDownloadLoading(false);
    }
  };
  
  // Format assessment data for download
  const formatAssessmentForDownload = (data) => {
    if (!data || !data.assessment || !data.details) {
      return 'Error: Assessment data is incomplete';
    }
    
    const { assessment, details } = data;
    const { title, organization, createdBy, createdAt, status } = assessment;
    const { categories, answers, notes } = details;
    
    let output = '';
    
    // Header information
    output += '=== ASSESSMENT REPORT ===\n\n';
    output += `Title: ${title || 'Untitled Assessment'}\n`;
    output += `Organization: ${organization?.name || assessment.organizationName || 'N/A'}\n`;
    output += `Created By: ${createdBy?.name || 'N/A'}\n`;
    output += `Date Created: ${formatDate(createdAt)}\n`;
    output += `Status: ${status === 'completed' ? 'Completed' : status === 'in_progress' ? 'In Progress' : 'Not Started'}\n\n`;
    
    // Categories, questions, and answers
    if (categories && Array.isArray(categories)) {
      categories.forEach((category, catIndex) => {
        output += `\n=== CATEGORY: ${category.name} ===\n`;
        
        if (category.standards && Array.isArray(category.standards)) {
          category.standards.forEach((standard, stdIndex) => {
            output += `\nSTANDARD: ${standard.name}\n`;
            output += `Description: ${standard.description || 'No description'}\n`;
            
            if (standard.questions && Array.isArray(standard.questions)) {
              standard.questions.forEach((question, qIndex) => {
                const questionId = question._id || question.id;
                const answer = answers && answers[questionId] ? answers[questionId] : 'Not Answered';
                const questionNote = notes && notes[questionId] ? notes[questionId] : '';
                
                output += `\nQ${catIndex+1}.${stdIndex+1}.${qIndex+1}: ${question.text}\n`;
                output += `Answer: ${answer}\n`;
                if (questionNote) {
                  output += `Note: ${questionNote}\n`;
                }
              });
            }
          });
        }
      });
    }
    
    return output;
  };
  
  // Render loading state
  if (loading) {
    return (
      <div className="admin-section">
        <h2 className="admin-section-title">
          <FaClipboardList /> Assessment Reports
        </h2>
        <div className="admin-loading">Loading assessment data...</div>
      </div>
    );
  }
  
  // Render error state
  if (error) {
    return (
      <div className="admin-section">
        <h2 className="admin-section-title">
          <FaClipboardList /> Assessment Reports
        </h2>
        <div className="admin-error">{error}</div>
        <button 
          className="admin-btn admin-btn-primary" 
          onClick={() => window.location.reload()}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  // Render assessment details view
  const renderAssessmentDetails = () => {
    if (loading) {
      return <div className="admin-loading">Loading assessment details...</div>;
    }
    
    if (error) {
      return <div className="admin-error">{error}</div>;
    }
    
    if (!currentAssessment) {
      return <div className="admin-error">Assessment not found</div>;
    }
    
    return (
      <div className="assessment-details">
        <div className="assessment-details-header">
          <h2>{currentAssessment.title || 'Untitled Assessment'}</h2>
          <div className="assessment-meta">
            <div className="assessment-meta-item">
              <strong>Organization:</strong> {currentAssessment.organization?.name || currentAssessment.organizationName || 'N/A'}
            </div>
            <div className="assessment-meta-item">
              <strong>Created By:</strong> {currentAssessment.createdBy?.name || 'N/A'}
            </div>
            <div className="assessment-meta-item">
              <strong>Date Created:</strong> {formatDate(currentAssessment.createdAt)}
            </div>
            <div className="assessment-meta-item">
              <strong>Last Updated:</strong> {formatDate(currentAssessment.updatedAt)}
            </div>
            <div className="assessment-meta-item">
              <strong>Status:</strong> 
              <span className={`admin-status-badge admin-status-${currentAssessment.status || 'not_started'}`}>
                {currentAssessment.status === 'completed' ? 'Completed' : 
                 currentAssessment.status === 'in_progress' ? 'In Progress' : 'Not Started'}
              </span>
            </div>
          </div>
          <button 
            className="admin-action-btn download-btn"
            onClick={() => handleDownloadReport(currentAssessment._id, currentAssessment.title)}
            disabled={downloadLoading}
          >
            <FaDownload /> {downloadLoading ? 'Downloading...' : 'Download Report'}
          </button>
        </div>
        
        {assessmentDetails ? (
          <div className="assessment-content">
            {assessmentDetails.categories && assessmentDetails.categories.map((category, catIndex) => (
              <div key={category._id || catIndex} className="assessment-category">
                <h3>{category.name}</h3>
                {category.standards && category.standards.map((standard, stdIndex) => (
                  <div key={standard._id || stdIndex} className="assessment-standard">
                    <h4>{standard.name}</h4>
                    <p>{standard.description}</p>
                    {standard.questions && standard.questions.map((question, qIndex) => {
                      const questionId = question._id || question.id;
                      const answer = assessmentDetails.answers && assessmentDetails.answers[questionId] 
                        ? assessmentDetails.answers[questionId] 
                        : 'Not Answered';
                      const questionNote = assessmentDetails.notes && assessmentDetails.notes[questionId] 
                        ? assessmentDetails.notes[questionId] 
                        : '';
                      
                      return (
                        <div key={questionId || qIndex} className="assessment-question">
                          <div className="question-text">
                            <strong>Q{catIndex+1}.{stdIndex+1}.{qIndex+1}:</strong> {question.text}
                          </div>
                          <div className="question-answer">
                            <strong>Answer:</strong> {answer}
                          </div>
                          {questionNote && (
                            <div className="question-note">
                              <strong>Note:</strong> {questionNote}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="admin-loading">Loading assessment content...</div>
        )}
      </div>
    );
  };
  
  // Main render method
  return (
    <div className="admin-container">
      <div className="admin-header">
        <Link to={viewMode === 'details' ? "/admin/reports" : "/admin/dashboard"} className="admin-back-link">
          <FaArrowLeft /> {viewMode === 'details' ? 'Return to Assessment Reports' : 'Return to Admin Dashboard'}
        </Link>
        <h1>
          <FaClipboardList className="admin-header-icon" /> 
          {viewMode === 'details' ? 'Assessment Details' : 'Assessment Reports'}
        </h1>
      </div>
      
      {viewMode === 'details' ? renderAssessmentDetails() : (
        <>
        {/* Filters and search */}
        <div className="admin-filters">
          <div className="admin-search">
            <FaSearch className="admin-search-icon" />
            <input
              type="text"
              placeholder="Search assessments..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="admin-search-input"
            />
          </div>
          
          <div className="admin-filter">
            <label className="admin-filter-label">
              <FaBuilding /> Organization:
            </label>
            <select 
              value={selectedOrgId} 
              onChange={handleOrgFilterChange}
              className="admin-select"
            >
              <option value="all">All Organizations</option>
              {organizations.map(org => (
                <option key={org._id} value={org._id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="admin-filter">
            <label className="admin-filter-label">
              <FaFilter /> Status:
            </label>
            <select 
              value={selectedStatus} 
              onChange={handleStatusFilterChange}
              className="admin-select"
            >
              <option value="all">All Statuses</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="not_started">Not Started</option>
            </select>
          </div>
        </div>
        
        {/* Assessment table */}
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Assessment Title</th>
                <th>Organization</th>
                <th>Created By</th>
                <th>Date Created</th>
                <th>Last Updated</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="admin-loading">
                    Loading assessment data...
                  </td>
                </tr>
              ) : !Array.isArray(filteredAssessments) ? (
                <tr>
                  <td colSpan="7" className="admin-no-data">
                    Error: Assessment data is invalid
                  </td>
                </tr>
              ) : filteredAssessments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="admin-no-data">
                    {searchTerm || selectedOrgId !== 'all' || selectedStatus !== 'all' 
                      ? 'No assessments match your search criteria' 
                      : 'No assessments found'}
                  </td>
                </tr>
              ) : (
                filteredAssessments.map(assessment => (
                  <tr key={assessment._id}>
                    <td>{assessment.title || 'Untitled Assessment'}</td>
                    <td>{assessment.organization?.name || assessment.organizationName || 'N/A'}</td>
                    <td>{assessment.createdBy?.name || 'N/A'}</td>
                    <td>{formatDate(assessment.createdAt)}</td>
                    <td>{formatDate(assessment.updatedAt)}</td>
                    <td>
                      <span className={`admin-status-badge admin-status-${assessment.status || 'not_started'}`}>
                        {assessment.status === 'completed' ? 'Completed' : 
                         assessment.status === 'in_progress' ? 'In Progress' : 'Not Started'}
                      </span>
                    </td>
                    <td>
                      <div className="admin-actions">
                        <Link
                          to={`/admin/assessments/${assessment._id}`}
                          className="admin-action-btn"
                          title="View Assessment Details"
                        >
                          <FaClipboardList />
                        </Link>
                        <button
                          className="admin-action-btn"
                          onClick={() => handleDownloadReport(assessment._id, assessment.title)}
                          title="Download Report"
                          disabled={downloadLoading}
                        >
                          <FaDownload />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Summary statistics */}
        <div className="admin-summary-stats">
          <div className="admin-summary-stat">
            <span className="admin-summary-label">Total Assessments:</span>
            <span className="admin-summary-value">{filteredAssessments.length}</span>
          </div>
          <div className="admin-summary-stat">
            <span className="admin-summary-label">Completed:</span>
            <span className="admin-summary-value">
              {filteredAssessments.filter(a => a.status === 'completed').length}
            </span>
          </div>
          <div className="admin-summary-stat">
            <span className="admin-summary-label">In Progress:</span>
            <span className="admin-summary-value">
              {filteredAssessments.filter(a => a.status === 'in_progress').length}
            </span>
          </div>
        </div>
        </>
      )}
    </div>
  );
};

// Add CSS for assessment details view
const styles = document.createElement('style');
styles.innerHTML = `
  .assessment-details {
    background-color: #FFFAFA;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .assessment-details-header {
    border-bottom: 2px solid #D2BA7B;
    padding-bottom: 15px;
    margin-bottom: 20px;
  }
  
  .assessment-details-header h2 {
    color: #243444;
    margin-bottom: 15px;
  }
  
  .assessment-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 15px;
  }
  
  .assessment-meta-item {
    background-color: #f5f5f5;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 14px;
  }
  
  .download-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #243444;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
  }
  
  .download-btn:hover {
    background-color: #1a2632;
  }
  
  .download-btn:disabled {
    background-color: #4C5454;
    cursor: not-allowed;
  }
  
  .assessment-category {
    margin-bottom: 30px;
  }
  
  .assessment-category h3 {
    color: #243444;
    border-bottom: 1px solid #D2BA7B;
    padding-bottom: 8px;
    margin-bottom: 15px;
  }
  
  .assessment-standard {
    background-color: #f9f9f9;
    padding: 15px;
    border-radius: 6px;
    margin-bottom: 20px;
  }
  
  .assessment-standard h4 {
    color: #243444;
    margin-bottom: 10px;
  }
  
  .assessment-question {
    background-color: white;
    border-left: 3px solid #D2BA7B;
    padding: 12px;
    margin: 15px 0;
    border-radius: 0 4px 4px 0;
  }
  
  .question-text {
    margin-bottom: 8px;
  }
  
  .question-answer {
    margin-bottom: 8px;
  }
  
  .question-note {
    font-style: italic;
    color: #4C5454;
  }
`;
document.head.appendChild(styles);

export default AssessmentReports;
